import {
  capitalize,
  capitalizeFirst,
  dateFormat,
  getPrimePendingMonthYear,
  validateEmail,
} from '@bloomays-lib/utils.shared';
import { Auth0User } from '@bloomays-lib/types.shared';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { startOfMonth } from 'date-fns/startOfMonth';
import {
  MissionPrimeLine,
  MonthlyPrimeHeaderAmounts,
  MonthlyTamPrimeAggregate,
  MonthlyTamPrimeQueryResult,
  MonthlyTamPrimeValidationInput,
  TamPrimeLine,
  isValidatedMonthlyTamPrime,
} from '../types/primes';

const ELIGIBLE_CRA_STATUSES = ['Validé client', 'Forcé Bloomays'] as const;

const VALIDATION_NON_BLOCKING_CRA_STATUSES = ['Validé client', 'Forcé Bloomays', 'Non travaillé'] as const;

export const isMissionPrimeLine = (line: TamPrimeLine): line is MissionPrimeLine => 'craStatus' in line;

export const isEligibleCraStatus = (craStatus: string): boolean =>
  ELIGIBLE_CRA_STATUSES.some((status) => status === craStatus);

export const isValidationNonBlockingCraStatus = (craStatus: string): boolean =>
  VALIDATION_NON_BLOCKING_CRA_STATUSES.some((status) => status === craStatus);

export const isPlainCraStatusDisplay = (craStatus: string): boolean => isValidationNonBlockingCraStatus(craStatus);

export const shouldShowMissionActivitiesLinkForCraStatus = (craStatus: string): boolean =>
  !isPlainCraStatusDisplay(craStatus);

const hasMissionWorkedDays = (line: MissionPrimeLine): boolean =>
  line.normalWorkedDays + line.saturdaysWorkedDays + line.sundayAndholidaysWorkedDays + line.astreinteWorkedDays > 0;

/** Ligne mission dont le CRA bloque le bouton Valider. */
const isMissionPrimeBlockingCraValidation = (line: MissionPrimeLine): boolean => {
  if (isValidationNonBlockingCraStatus(line.craStatus)) {
    return false;
  }
  if (line.craStatus === 'Non créé') {
    return true;
  }
  if (!hasMissionWorkedDays(line)) {
    return false;
  }
  return true;
};

/** Toujours 2 décimales : ce sont des euros, les centimes comptent (cohérent avec les notifications Slack). */
const PRIME_EURO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

export const formatPrimeEuro = (value: number): string => `${value.toLocaleString('fr-FR', PRIME_EURO_DECIMALS)} €`;

export const formatPrimeEuroWithSign = (value: number): string => {
  if (value < 0) {
    return `- ${Math.abs(value).toLocaleString('fr-FR', PRIME_EURO_DECIMALS)} €`;
  }
  if (value > 0) {
    return `+ ${value.toLocaleString('fr-FR', PRIME_EURO_DECIMALS)} €`;
  }
  return formatPrimeEuro(value);
};

export const splitPrimeMonthLabel = (monthLabel: string): { monthName: string; year: string } => {
  const parts = monthLabel.trim().split(/\s+/);
  if (parts.length <= 1) {
    return { monthName: monthLabel, year: '' };
  }
  const year = parts[parts.length - 1] ?? '';
  const monthName = parts.slice(0, -1).join(' ');
  return { monthName, year };
};

export const formatPrimeRate = (rate: number): string => `${(rate * 100).toLocaleString('fr-FR')} %`;

export const deriveHeaderAmounts = (aggregate: MonthlyTamPrimeQueryResult): MonthlyPrimeHeaderAmounts => ({
  freelanceMarge: aggregate.totalFreelanceMarge,
  billingMarge: aggregate.totalBillingMarge,
  // The header must reflect the persisted aggregate totals, even when line details were not retrofixed.
  commission: aggregate.totalPrime - aggregate.bonus - aggregate.regularisation,
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  regularisationReason: aggregate.regularisationReason ?? null,
  total: aggregate.totalPrime,
});

export const getTamDisplayName = (user: Auth0User | undefined): string => {
  if (!user) {
    return '';
  }
  const given = user.given_name?.trim();
  const family = user.family_name?.trim();
  if (given && family) {
    return `${given} ${family}`;
  }
  if (user.displayName?.trim()) {
    return user.displayName.trim();
  }
  if (user.name?.trim()) {
    return user.name.trim();
  }
  return user.email.trim();
};

/** Preview temporaire — 15 du mois pour éviter les décalages fuseau horaire côté API. */
export const toPrimeReferenceDate = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 15);

const MM_YYYY_PARAM = /^(0[1-9]|1[0-2])\d{4}$/;

export const isValidMonthYear = (mmYyyy: string): boolean => MM_YYYY_PARAM.test(mmYyyy.trim());

/** Active state for TAM nav « Mensuel » — default route or deep-link `/primes/MMyyyy`. */
export const isTamMonthlyPrimesNavActive = (pathname: string): boolean => {
  if (pathname === '/primes/mois') {
    return true;
  }
  if (!pathname.startsWith('/primes/')) {
    return false;
  }
  const segment = pathname.slice('/primes/'.length);
  return isValidMonthYear(segment);
};

export const parseMonthYearToDate = (mmYyyy: string): Date => {
  const parsed = parse(mmYyyy.trim(), 'MMyyyy', new Date());
  return toPrimeReferenceDate(startOfMonth(parsed));
};

export const formatReferenceDateToMonthYear = (referenceDate: Date): string => format(referenceDate, 'MM/yyyy');

/** Pending preload from annual navigation only targets the current prime pending month. */
export const doesPreloadedMonthlyTamPrimeMatchReferenceMonth = (
  preloaded: MonthlyTamPrimeQueryResult,
  referenceDate: Date,
): boolean => {
  const expectedMonthYear = formatReferenceDateToMonthYear(referenceDate);
  if (isValidatedMonthlyTamPrime(preloaded)) {
    return preloaded.monthYear === expectedMonthYear;
  }
  return expectedMonthYear === getPrimePendingMonthYear();
};

export const formatDateToMonthYear = (referenceDate: Date): string => format(referenceDate, 'MMyyyy');

export const parseMonthlyPrimeTamSearchParam = (
  searchParams: URLSearchParams,
  isSystem: boolean,
): string | undefined => {
  if (!isSystem) {
    return undefined;
  }
  const tam = searchParams.get('tam')?.trim() ?? '';
  if (!tam || !validateEmail(tam)) {
    return undefined;
  }
  return tam;
};

export const formatPrimeValidationDate = (validationDate: Date | string): string =>
  capitalize(dateFormat(new Date(validationDate), 'd MMMM yyyy'));

export const formatMonthYearToMonthLabel = (monthYear: string): string =>
  capitalizeFirst(dateFormat(parse(monthYear, 'MM/yyyy', new Date()), 'MMMM'));

export const isPrimeLineError = (line: TamPrimeLine): boolean => line.errorCode != null;

export const hasPrimeLineErrors = (aggregate: MonthlyTamPrimeQueryResult): boolean =>
  aggregate.primes.some(isPrimeLineError);

export const hasIneligibleMissionCras = (aggregate: MonthlyTamPrimeQueryResult): boolean =>
  aggregate.primes.filter(isMissionPrimeLine).some(isMissionPrimeBlockingCraValidation);

export const canValidateMonthlyPrime = (
  aggregate: MonthlyTamPrimeQueryResult,
): aggregate is MonthlyTamPrimeAggregate => {
  if (isValidatedMonthlyTamPrime(aggregate)) {
    return false;
  }
  // Un mois n'est validable que s'il y a quelque chose à enregistrer : une prime OU de la marge.
  // Cas Director (PRO-1367) : tant que le cumul est dans la tranche 0 %, la prime vaut 0 € alors que la marge
  // existe et doit être validée (elle alimente le cumul annuel ; validation obligatoire avant export).
  if (aggregate.totalPrime <= 0 && aggregate.totalMarge <= 0) {
    return false;
  }
  if (hasPrimeLineErrors(aggregate)) {
    return false;
  }
  return !hasIneligibleMissionCras(aggregate);
};

export const mapAggregateToValidationInput = (aggregate: MonthlyTamPrimeAggregate): MonthlyTamPrimeValidationInput => ({
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  totalFreelanceMarge: aggregate.totalFreelanceMarge,
  totalBillingMarge: aggregate.totalBillingMarge,
  totalMarge: aggregate.totalMarge,
  totalPrime: aggregate.totalPrime,
  totalDeals: aggregate.totalDeals,
  totalMissions: aggregate.totalMissions,
  primes: aggregate.primes.map((line) => {
    const base = {
      jobFunnelProjectId: line.jobFunnelProjectId,
      monthlyMargin: line.monthlyMargin,
      monthlyPrime: line.monthlyPrime,
      splitRate: line.splitRate,
      commissionRate: line.commissionRate,
      carryInSourceMonthYear: line.carryInSourceMonthYear,
    };
    // Ligne reportée : passe-plat, le back l'ignore à la persistance — pas d'exigence CRA.
    if (line.carryInSourceMonthYear != null) {
      return { ...base, craAirtableId: null };
    }
    if (isMissionPrimeLine(line)) {
      if (typeof line.craAirtableId !== 'string' || line.craAirtableId.length === 0) {
        throw new Error('Mission prime line is missing craAirtableId for validation');
      }

      return {
        ...base,
        additionalData: {
          normalWorkedDays: line.normalWorkedDays,
          saturdaysWorkedDays: line.saturdaysWorkedDays,
          sundayAndholidaysWorkedDays: line.sundayAndholidaysWorkedDays,
          astreinteWorkedDays: line.astreinteWorkedDays,
        },
        craAirtableId: line.craAirtableId,
      };
    }
    return { ...base, craAirtableId: null };
  }),
});
